<template>
    <div class="billing columns is-multiline mx-2">
        <settings-sidebar :active-menu-item="'billing'"
                          class="column is-3 py-2 mr-md-3 mb-3 mb-md-auto"></settings-sidebar>
        <div class="column mb-3 p-0" v-if="user.eod_technician || user.technical_interest">
            <div v-if="hasPastDueInvoices" class="card p-3 px-2 px-lg-6 mb-3">
                <pay-past-due-invoices
                    :text="true"
                    @pay-error="payPastDueError"
                    @success="flashPaySuccess()"
                />
            </div>
            <div class="card p-3 px-2 px-lg-6 mb-3">
                <div v-if="show_pay_success" class="alert alert-success my-2">
                    Thank you, your payment was processed successfully.
                </div>
                <h1 class="h2 t-orange ml-1 mb-3">Billing Information</h1>
                <form method="POST" action="#" class="#">
                    <div v-if="errors" class="errors mb-4">
                        <template v-if="Array.isArray(errors)" v-for="(field) in errors">
                            <div v-for="(error) in field" class="error">{{error}}</div>
                        </template>
                    </div>
                    <div class="billing-details d-flex flex-column justify-content-center">
                        <div class="columns">
                            <div class="column is-half">
                                <div class="field mb-4" :class="{ 'has-error': $v.form.billingAddress.$error }">
                                    <label class="label mb-1" for="billingAddress">First line of address*</label>
                                    <div class="control">
                                        <input id="billingAddress" name="billing_address" class="input" type="text"
                                               v-model.trim="$v.form.billingAddress.$model"/>
                                    </div>
                                    <div class="error" v-if="$v.$dirty && $v.form.billingAddress.$error">
                                        {{getErrorMessage($v.form.billingAddress)}}
                                    </div>
                                </div>
                            </div>
                            <div class="column is-half">
                                <div class="field mb-4">
                                    <label class="label mb-1" for="billingAddressLine2">Second line of address</label>
                                    <div class="control">
                                        <input id="billingAddressLine2" name="billing_address_line_2" class="input"
                                               type="text"
                                               v-model.trim="$v.form.billingAddressLine2"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column is-half">
                                <div class="field mb-4" :class="{ 'has-error': $v.form.billingCountry.$error }">
                                    <label class="label mb-1" for="billingCountry">Country*</label>
                                    <div class="control">
                                        <select id="billingCountry" name="billing_country" class="select"
                                                v-model.trim="$v.form.billingCountry.$model">
                                            <option value="null" selected>Country</option>
                                            <option v-for="(country) in countries" :value="country.code">
                                                {{country.name}}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="error" v-if="$v.$dirty && $v.form.billingCountry.$error">
                                        {{getErrorMessage($v.form.billingCountry)}}
                                    </div>
                                </div>
                            </div>
                            <div class="column is-half">
                                <div class="field mb-4" :class="{ 'has-error': $v.form.billingPostcode.$error }">
                                    <label class="label mb-1" for="billingPostcode">Postcode*</label>
                                    <div class="control">
                                        <input id="billingPostcode" name="billingPostcode" class="input" type="text"
                                               v-model.trim="$v.form.billingPostcode.$model"/>
                                    </div>
                                    <div class="error" v-if="$v.$dirty && $v.form.billingPostcode.$error">
                                        {{getErrorMessage($v.form.billingPostcode)}}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column is-half">
                                <div class="field mb-4" :class="{ 'has-error': $v.form.cardholderName.$error }">
                                    <label class="label mb-1" for="cardholderName">Cardholder’s Name*</label>
                                    <div class="control">
                                        <input id="cardholderName" name="cardholder_name" class="input" type="text"
                                               v-model.trim="$v.form.cardholderName.$model"/>
                                    </div>
                                    <div class="error" v-if="$v.$dirty && $v.form.cardholderName.$error">
                                        {{getErrorMessage($v.form.cardholderName)}}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column is-full">
                                <div class="field mb-4">
                                    <div class="d-flex justify-content-between">
                                        <label for="cardnumber" class="label required MB-1">
                                            Card Details*
                                        </label>
                                        <p class="text-right fs-14 mb-1">{{localUser.card_brand}} ************{{localUser.card_last_four}}</p>
                                    </div>
                                    <div ref="card" class="stripe-card"></div>
                                    <span
                                        id="card-errors"
                                        class="error mt-1"></span>
                                </div>
                            </div>
                        </div>
                        <div class="columns">
                            <div class="column is-offset-3 is-9 is-offset-lg-7 is-lg-5">
                                <div v-if="billingUpdated" class="alert alert-success mb-2">Billing information successfully
                                    updated
                                </div>
                                <div v-if="errors && !Array.isArray(errors)" class="alert alert-error mb-2">{{errors}}</div>

                                <div class="field mx-auto text-right">
                                    <button v-if="!loading" @click.prevent="update()" class="btn btn-form"
                                            :disabled='paying'>
                                        Update Billing Information
                                    </button>
                                    <pulse-loader v-if="loading" :loading="true" :color="'#E57722'"
                                                  :size="'20px'"></pulse-loader>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
            <cat-uxo-billing-update-subscription :plans='plans'></cat-uxo-billing-update-subscription>
        </div>
        <div class="second column mb-3 p-0" v-else>
            <re-subscribe :plans='plans' />
        </div>
    </div>
</template>

<script>
    import SettingsSidebar from '../../shared/SettingsSidebar';
    import Subscriptions from '../../Subscriptions'
    import CatUxoBillingUpdateSubscription from './UpdateSubScription';
    import PayPastDueInvoices from './PayPastDueInvoices';
    import ReSubscribe from './ReSubscribe';
    import MixinModal from '../../../mixins/modal'
    import {axiosApi, axiosWeb} from "@/js/axios";
    import {required} from 'vuelidate/lib/validators';
    import {Card, createToken} from 'vue-stripe-elements-plus'
    import {mapActions, mapGetters, mapState} from "vuex";
    import PulseLoader from 'vue-spinner/src/PulseLoader.vue';

    import {loadStripe} from '@stripe/stripe-js';

    export default {
        name: 'CatUxoPageBilling',
        components: {
            CatUxoBillingUpdateSubscription,
            SettingsSidebar,
            Subscriptions,
            Card,
            PulseLoader,
            PayPastDueInvoices,
            ReSubscribe
        },
        props: {
            user: {
                type: Object,
                required: true
            },
            plans: {
                type: Array,
                required: true
            }
        },
        mixins: [MixinModal],
        data() {
            return {
                form: {
                    csrfToken: null,
                    billingAddress: this.user.billing_address,
                    billingAddressLine2: this.user.billing_address_line_2,
                    billingCountry: this.user.billing_country,
                    billingPostcode: this.user.billing_zip,
                    cardholderName: null,
                    billingCard: null,
                },
                countries: [],
                complete: false,
                loading: false,
                billingUpdated: false,
                selectedPlan: null,
                errors: [],

                show_pay_success: false,

                stripe: null,
                elements: null,
                card: null,
            }
        },
        computed: {
            ...mapState('shared', [
                'stripeKey',
                'stripeOptions'
            ]),
            ...mapGetters('form', [
                'getErrorMessage'
            ]),
            ...mapState('user', {
                subscription: 'subscription',
                paying: 'paying_invoices'
            }),
            ...mapGetters('user', {
                localUser: 'activeUser',
                currentSubscriptionPeriodEnd: 'currentSubscriptionPeriodEnd',
                hasPastDueInvoices: 'hasPastDueInvoices'
            }),
            rules() {
                return {
                    billingAddress: {
                        required,
                    },
                    billingCountry: {
                        required,
                    },
                    billingPostcode: {
                        required,
                    },
                    cardholderName: {
                        required,
                    },
                }
            },
        },
        watch: {
            user: {
                immediate: true,
                deep: true,
                handler(val) {
                    console.log('User', val);
                }
            }
        },
        methods: {
            async loadStripe() {
                this.stripe = await loadStripe(this.stripeKey);

                this.elements = this.stripe.elements();
                this.card = this.elements.create('card', {style: this.style});
                this.card.mount(this.$refs.card);
            },

            ...mapActions('user', [
                'setUser'
            ]),

            payPastDueError(message) {
                let errorElement = document.getElementById('card-errors');
                errorElement.textContent = message;
            },
            flashPaySuccess() {
                const $this = this;
                this.show_pay_success = true;
                setTimeout(() => $this.show_pay_success = false, 3000);
            },
            fetchCountries() {
                return axiosApi.get(`/api/v1/countries`)
                    .then(response => {
                        if (response.data) {
                            this.countries = response.data.data
                        }
                    })
                    .catch(error => {
                        console.log(error);
                    })
            },
            // submit() {
            //     let $this = this;
            //     $this.$v.form.$touch();
            //     if ($this.$v.form.$invalid) return;
            //
            //     $this.errors = [];
            //     createToken().then(function (data) {
            //         data.token.card.name = $this.form.cardholderName;
            //         data.token.card.address_line1 = $this.form.billingAddress;
            //         data.token.card.address_line2 = $this.form.billingAddressLine2;
            //         data.token.card.address_country = $this.form.billingCountry;
            //         data.token.card.address_zip = $this.form.billingPostcode;
            //
            //         if (data.error) {
            //             $this.loading = false;
            //
            //             // Inform the user if there was an error.
            //             let errorElement = document.getElementById('card-errors');
            //             errorElement.textContent = data.error.message;
            //
            //             return;
            //         }
            //
            //         // Send the token to your server.
            //         // Display the loading on the button
            //         $this.loading = true;
            //
            //         // Add token data used to make request to the API
            //         $this.form.source = data.token;
            //
            //         // Subscribe the user
            //         return axiosWeb.post(`/api/v1/update-billing/${$this.user.id}`, $this.form).then(response => {
            //
            //             $this.setUser({user: response.data});
            //             $this.loading = false;
            //             $this.billingUpdated = true;
            //
            //             setTimeout(() => $this.billingUpdated = false, 3000);
            //         }).catch((error) => {
            //             $this.loading = false;
            //
            //             console.log(error);
            //
            //             if (error.response.status === 422) {
            //                 $this.errors = error.response.data.errors;
            //             }
            //
            //             if (error.response.status === 500) {
            //                 $this.errors = error.response.data;
            //                 setTimeout(() => this.errors = false, 4000);
            //             }
            //         })
            //     });
            //
            // },

            update() {
                const $this = this;

                $this.$v.form.$touch();
                if ($this.$v.form.$invalid) {
                    return;
                }

                this.loading = true;

                this.errors = [];
                let errorElement = document.getElementById('card-errors');
                errorElement.textContent = '';

                this.stripe.createToken(this.card).then(function(data) {

                    if (data.error) {
                        let errorElement = document.getElementById('card-errors');
                        errorElement.textContent = data.error.message;

                        $this.loading = false;

                        return;
                    }

                    data.token.card.name = $this.form.cardholderName;
                    data.token.card.address_line1 = $this.form.billingAddress;
                    data.token.card.address_line2 = $this.form.billingAddressLine2;
                    data.token.card.address_country = $this.form.billingCountry;
                    data.token.card.address_zip = $this.form.billingPostcode;

                    $this.form.source = data.token;

                    $this.updateBillingDetails();
                });
            },

            updateBillingDetails() {
                const $this = this;

                return axiosWeb.post(`/api/v1/update-billing/${$this.user.id}`, $this.form).then(response => {

                    $this.setUser({user: response.data});
                    $this.loading = false;
                    $this.billingUpdated = true;

                    setTimeout(() => $this.billingUpdated = false, 3000);
                }).catch((error) => {
                    $this.loading = false;
                    if (error.response.status === 402) {
                        if (
                            error.response.data.hasOwnProperty('error') &&
                            error.response.data.error.hasOwnProperty('type') &&
                            error.response.data.error.type === 'card_error'
                        ) {
                            let errorElement = document.getElementById('card-errors');
                            errorElement.textContent = error.response.data.error.message;
                        }
                    }

                    if (error.response.status === 422) {
                        $this.errors = error.response.data.errors;
                    }

                    if (error.response.status === 500) {
                        $this.errors = error.response.data;
                        setTimeout(() => this.errors = false, 4000);
                    }
                })
            }
        },
        created() {
            this.csrfToken = document.querySelector('meta[name="csrf-token"]').content;
            this.fetchCountries();
            this.$store.dispatch('user/fetchSubscription',{});
            this.setUser({user: this.user});
        },
        mounted() {
            this.loadStripe();
        },
        validations() {
            return {
                form: this.rules
            }
        },
    }
</script>

<style lang="scss" scoped>
    @include media-breakpoint-down(sm) {
        .btn,
        .btn-form,
        .btn-link {
            width: 100%;
            max-width: 100%;
        }

        .card__subscription {
            text-align: center;
        }
    }

    @include media-breakpoint-between(md, lg) {
        .btn {
            font-size: rem(16px);
        }
    }
</style>



// WEBPACK FOOTER //
// resources/js/components/pages/settings/Billing.vue